/* eslint-disable no-octal-escape */
import React, { useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { makeStyles } from '@mui/styles';
import { Button, Typography, Grid, Box } from '@mui/material';
import { Page } from 'components';

import DateRangeIcon from '@mui/icons-material/DateRange';
import TableViewIcon from '@mui/icons-material/TableView';
import CellTowerIcon from '@mui/icons-material/CellTower';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import BackupTableIcon from '@mui/icons-material/BackupTable';
import WifiIcon from '@mui/icons-material/Wifi';
import BoltIcon from '@mui/icons-material/Bolt';
import NetworkCheckIcon from '@mui/icons-material/NetworkCheck';
import SignalWifiStatusbarConnectedNoInternet4Icon from '@mui/icons-material/SignalWifiStatusbarConnectedNoInternet4';
import SettingsIcon from '@mui/icons-material/Settings';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import Co2Icon from '@mui/icons-material/Co2';
import DownloadIcon from '@mui/icons-material/Download';
import UploadIcon from '@mui/icons-material/Upload';
import MoveToInboxIcon from '@mui/icons-material/MoveToInbox';

import { GetDatasetInfo, GetDatasetInfoEVO } from './actions/HomeActions';

import Evolution from './components/Evolution.js';

const useStyles = makeStyles((theme) => ({
	root: {
		flexDirection: 'column',
		alignItems: 'flex-end',
	},
	content: {
		// width: theme.breakpoints.values.md,
		// maxWidth: '95%',
		// margin: '0 auto',
		padding: '40px 40px 5px 40px',
		// [theme.breakpoints.up('md')]: {
		//	padding: '40px 24px',
		// },
	},
	logo: {
		marginTop: '75px',
		marginLeft: '100px',
		height: '200px'
	},
	icon: {
		color: '#1565c0',
		fontSize: '4rem',
		marginBottom: '0px',
	},
	h1: {
		fontWeight: '900',
		color: 'rgb(90, 90, 90)',
		textAlign: 'center',
		fontSize: '1.8rem',
		marginBottom: '0px',
	},
	h2: {
		color: '#1565c0',
		textAlign: 'left',
		fontWeight: '100',
		[theme.breakpoints.down('md')]: {
			textAlign: 'center'
		},
	},
	h5: {
		color: 'rgb(90, 90, 90)',
		textAlign: 'center',
		lineHeight: '20px',
		marginTop: '-3px',
		fontSize: '14px',
	},
	h25: {
		fontWeight: '500',
		fontSize: '16px',
		color: 'rgb(90, 90, 90)',
	},
	item: {
		marginLeft: '20px',
		marginRight: '20px',
		spacing: '5',
	},
	desc: {
		[theme.breakpoints.down('md')]: {
			justifyContent: 'center'
		},
		spacing: '5',
		marginBottom: '-30px',
	},
	button: {
		marginTop: '10px',
	},
	chartTitle: {
		color: '#1565c0',
		textAlign: 'center',
		fontWeight: '800',
		fontSize: '20px',
		margin: '15px 0px 10px 0px',
	},
}));

const Home = () => {
	const dispatch = useDispatch();
	const datasetInfo = useSelector(state => state.homeReducer.DatasetInfo);
	const datasetInfoEVO = useSelector(state => state.homeReducer.DatasetInfoEVO);
	const classes = useStyles();

	const _getDatasetInfo = useCallback(() => {
		dispatch(GetDatasetInfo());
	}, []);

	const _getDatasetInfoEVO = useCallback(() => {
		dispatch(GetDatasetInfoEVO());
	}, []);

	const openInNewTab = (url) => {
		window.open(url, '_blank', 'noreferrer');
	};

	useEffect(() => {
		_getDatasetInfo();
		_getDatasetInfoEVO();
	}, []);

	const weekEnd = datasetInfoEVO.map(data => data.weekEnd);
	const avgUDATCell = datasetInfoEVO.map(data => data.avgUDATCell);
	const avgUUATCell = datasetInfoEVO.map(data => data.avgUUATCell);
	const avgDRBURCell = datasetInfoEVO.map(data => data.avgDRBURCell);
	const avgConsTimeSite = datasetInfoEVO.map(data => data.avgConsTimeSite);
	const avgConsStorageSite = datasetInfoEVO.map(data => data.avgConsStorageSite);
	const avgCarbonSite = datasetInfoEVO.map(data => data.avgCarbonSite);

	return (
		<Page className={classes.root} title="Home">
			<div className={classes.content}>
				<Grid
					className={classes.line}
					container
					justifyContent="center"
					spacing={4.7}
				>
					<Grid item lg={2} md={12} xs={12} display="flex" justifyContent="flex-start" alignItems="flex-start" className={classes.desc}>
						<Typography gutterBottom variant="h2" className={classes.h2}>
							<br />
							DATASET SIZE
						</Typography>
					</Grid>
					<Grid item lg={10} md={12} xs={12} display="flex" justifyContent="center">
						<Grid item lg={4} md={4} sm={12} className={classes.item}>
							<Typography align="center">
								<CellTowerIcon className={classes.icon} />
							</Typography>
							<Typography align="center" gutterBottom variant="h1" className={classes.h1}>
								161 Cells 4G
							</Typography>
							<Typography align="center" component="h2" variant="h5" className={classes.h5}>
								22 sites
							</Typography>
						</Grid>
						<Grid item lg={4} md={4} sm={12} className={classes.item}>
							<Typography align="center">
								<WifiIcon className={classes.icon} />
							</Typography>
							<Typography align="center" gutterBottom variant="h1" className={classes.h1}>
								6 Bands
							</Typography>
							<Typography align="center" component="h2" variant="h5" className={classes.h5}>
								700 | 800 | 900 | 1800 | 2100 | 2600 MHz
							</Typography>
						</Grid>
						<Grid item lg={4} md={4} sm={12} className={classes.item}>
							<Typography align="center">
								<DateRangeIcon className={classes.icon} />
							</Typography>
							<Typography align="center" gutterBottom variant="h1" className={classes.h1}>
								{datasetInfo.qtyMonths}
							&nbsp;Months &&nbsp;
								{datasetInfo.qtyDays}
							&nbsp;days
							</Typography>
							<Typography align="center" component="h2" variant="h5" className={classes.h5}>
								collected data
							</Typography>
						</Grid>
					</Grid>

					<Grid item lg={2} md={12} xs={12} display="flex" justifyContent="flex-start" alignItems="flex-start" className={classes.desc}>
						<Typography gutterBottom variant="h2" className={classes.h2}>
							<br />
							DATASET FEATURES
						</Typography>
					</Grid>
					<Grid item lg={10} md={12} xs={12} display="flex" justifyContent="center">
						<Grid item lg={4} md={4} xs={12} className={classes.item}>
							<Typography align="center">
								<TableViewIcon className={classes.icon} />
							</Typography>
							<Typography align="center" gutterBottom variant="h1" className={classes.h1}>
								570 PM Counters
							</Typography>
							<Typography align="center" component="h2" variant="h5" className={classes.h5}>
								64 KPI
							</Typography>
						</Grid>
						<Grid item lg={4} md={4} xs={12} className={classes.item}>
							<Typography align="center">
								<NotificationsActiveIcon className={classes.icon} />
							</Typography>
							<Typography align="center" gutterBottom variant="h1" className={classes.h1}>
								{datasetInfo.qtyAlarms}
								k FM Alarms
							</Typography>
							<Typography align="center" component="h2" variant="h5" className={classes.h5}>
								{datasetInfo.qtyFaults}
								k Faults |&nbsp;
								{datasetInfo.qtyEvents}
								k Events |&nbsp;
								{datasetInfo.qtyAlarmNames}
								&nbsp;Alarm Types
							</Typography>
						</Grid>
						<Grid item lg={4} md={4} xs={12} className={classes.item}>
							<Typography align="center">
								<BackupTableIcon className={classes.icon} />
							</Typography>
							<Typography align="center" gutterBottom variant="h1" className={classes.h1}>
								2,7k CM Param.
							</Typography>
							<Typography align="center" component="h2" variant="h5" className={classes.h5}>
								145 MO's
							</Typography>
						</Grid>
					</Grid>

					<Grid item lg={2} md={12} xs={12} display="flex" justifyContent="flex-start" alignItems="flex-start" className={classes.desc}>
						<Typography gutterBottom variant="h2" className={classes.h2}>
							<br />
							DATASET INTEGRITY
						</Typography>
					</Grid>
					<Grid item lg={10} md={12} xs={12} display="flex" justifyContent="center">
						<Grid item lg={4} md={4} xs={12} className={classes.item}>
							<Typography align="center">
								<NetworkCheckIcon className={classes.icon} />
							</Typography>
							<Typography align="center" gutterBottom variant="h1" className={classes.h1}>
								{datasetInfo.validDataPM}
								%
							</Typography>
							<Typography align="center" component="h2" variant="h5" className={classes.h5}>
								PM Valid Data
							</Typography>
						</Grid>
						<Grid item lg={4} md={4} xs={12} className={classes.item}>
							<Typography align="center">
								<SignalWifiStatusbarConnectedNoInternet4Icon className={classes.icon} />
							</Typography>
							<Typography align="center" gutterBottom variant="h1" className={classes.h1}>
								{datasetInfo.validDataFM}
								%
							</Typography>
							<Typography align="center" component="h2" variant="h5" className={classes.h5}>
								FM Valid Data
							</Typography>
						</Grid>
						<Grid item lg={4} md={4} xs={12} className={classes.item}>
							<Typography align="center">
								<SettingsIcon className={classes.icon} />
							</Typography>
							<Typography align="center" gutterBottom variant="h1" className={classes.h1}>
								{datasetInfo.validDataCM}
								%
							</Typography>
							<Typography align="center" component="h2" variant="h5" className={classes.h5}>
								CM Valid Data
							</Typography>
						</Grid>
					</Grid>
					<Grid item lg={2} md={12} xs={12} display="flex" justifyContent="flex-start" alignItems="flex-start" className={classes.desc}>
						<Typography gutterBottom variant="h2" className={classes.h2}>
							<br />
							WEEKLY 4G CELL PERFORMANCE
						</Typography>
					</Grid>
					<Grid item lg={10} md={12} xs={12} display="flex" justifyContent="center">
						<Grid item lg={4} md={4} xs={12} className={classes.item}>
							<Typography align="center">
								<DownloadIcon className={classes.icon} />
							</Typography>
							<Typography align="center" gutterBottom variant="h1" className={classes.h1}>
								{datasetInfo.avgUDATCell}
								&nbsp;Mbit/s
							</Typography>
							<Typography align="center" component="h2" variant="h5" className={classes.h5}>
								Avg. User DL Throughput
							</Typography>
							<Box textAlign="center">
								<Evolution indicator="Avg. User DL Throughput | Mbits/s" weekEnd={weekEnd} indicatorValues={avgUDATCell} />
							</Box>
						</Grid>
						<Grid item lg={4} md={4} xs={12} className={classes.item}>
							<Typography align="center">
								<UploadIcon className={classes.icon} />
							</Typography>
							<Typography align="center" gutterBottom variant="h1" className={classes.h1}>
								{datasetInfo.avgUUATCell}
								&nbsp;Mbit/s
							</Typography>
							<Typography align="center" component="h2" variant="h5" className={classes.h5}>
								Avg. User UL Throughput
							</Typography>
							<Box textAlign="center">
								<Evolution indicator="Avg. User UL Throughput | Mbits/s" weekEnd={weekEnd} indicatorValues={avgUUATCell} />
							</Box>
						</Grid>
						<Grid item lg={4} md={4} xs={12} className={classes.item}>
							<Typography align="center">
								<MoveToInboxIcon className={classes.icon} />
							</Typography>
							<Typography align="center" gutterBottom variant="h1" className={classes.h1}>
								{datasetInfo.avgDRBURCell}
								%
							</Typography>
							<Typography align="center" component="h2" variant="h5" className={classes.h5}>
								Avg. DL PRB
							</Typography>
							<Box textAlign="center">
								<Evolution indicator="Avg. DL PRB | %" weekEnd={weekEnd} indicatorValues={avgDRBURCell} />
							</Box>
						</Grid>
					</Grid>
					<Grid item lg={2} md={12} xs={12} display="flex" justifyContent="flex-start" alignItems="flex-start" className={classes.desc}>
						<Typography gutterBottom variant="h2" className={classes.h2}>
							<br />
							WEEKLY ECO INDICATORS
						</Typography>
					</Grid>
					<Grid item lg={10} md={12} xs={12} display="flex" justifyContent="center">
						<Grid item lg={4} md={4} xs={12} className={classes.item}>
							<Typography align="center">
								<BoltIcon className={classes.icon} />
							</Typography>
							<Typography align="center" gutterBottom variant="h1" className={classes.h1}>
								{datasetInfo.avgConsTimeSite}
								&nbsp;kWh
							</Typography>
							<Typography align="center" component="h2" variant="h5" className={classes.h5}>
								Avg. Cons./Site (excl. 2G)
							</Typography>
							<Box textAlign="center">
								<Evolution indicator="Avg. Cons./Site (excl. 2G) | kWh" weekEnd={weekEnd} indicatorValues={avgConsTimeSite} />
							</Box>
						</Grid>
						<Grid item lg={4} md={4} xs={12} className={classes.item}>
							<Typography align="center">
								<FileCopyIcon className={classes.icon} />
							</Typography>
							<Typography align="center" gutterBottom variant="h1" className={classes.h1}>
								{datasetInfo.avgConsStorageSite}
								&nbsp;kWh/TB
							</Typography>
							<Typography align="center" component="h2" variant="h5" className={classes.h5}>
								Avg. Consumption/TB (only 4G)
							</Typography>
							<Box textAlign="center">
								<Evolution indicator="Avg. Consumption/TB (only 4G) | kWh/TB" weekEnd={weekEnd} indicatorValues={avgConsStorageSite} />
							</Box>
						</Grid>
						<Grid item lg={4} md={4} xs={12} className={classes.item}>
							<Typography align="center">
								<Co2Icon className={classes.icon} />
							</Typography>
							<Typography align="center" gutterBottom variant="h1" className={classes.h1}>
								{datasetInfo.avgCarbonSite}
								&nbsp;Kg
							</Typography>
							<Typography align="center" component="h2" variant="h5" className={classes.h5}>
								Avg. CO2/Site (excl. 2G)
							</Typography>
							<Box textAlign="center">
								<Evolution indicator="Avg. CO2/Site (excl. 2G) | Kg" weekEnd={weekEnd} indicatorValues={avgCarbonSite} />
							</Box>
						</Grid>
					</Grid>
				</Grid>
			</div>
		</Page>
	);
};

export default Home;
